import { Component } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

/**
 * ErrorBoundary — catches any JS error thrown inside a page subtree and
 * shows a graceful fallback instead of a black screen.
 *
 * React requires a class component for componentDidCatch. We log the
 * error to the console (so admins can grep Railway logs) and render
 * a soft error card with three escape paths: retry, go to dashboard,
 * reload.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomePage />
 *   </ErrorBoundary>
 *
 * Or wrap the whole MainLayout outlet so every authenticated page is
 * protected at once.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, prevResetKey: props.resetKey };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  // Al cambiar de ruta (resetKey), limpiamos el error para que la leyenda
  // "No pudimos cargar esta sección" no se quede pegada al navegar a otro panel.
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.prevResetKey) {
      return { hasError: false, error: null, prevResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, info) {
    // Log so the error shows up in browser devtools and any error tracker
    console.error('ErrorBoundary caught a render error:', error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message =
      this.state.error?.message ||
      'Algo se rompió al renderizar esta sección.';

    // Destino "home" configurable por layout (usuario/coach/admin) para no
    // sacar a un coach/admin de su panel ante un error de render.
    const homeTo = this.props.homeTo || '/app/dashboard';
    const homeLabel = this.props.homeLabel || 'Ir al dashboard';

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <Card className="card-default border-amber-500/40 max-w-lg w-full">
          <CardContent className="p-8 space-y-4 text-center">
            <div className="w-14 h-14 mx-auto rounded-sm bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-amber-400" />
            </div>
            <h2 className="font-teko text-2xl text-white uppercase">
              No pudimos cargar esta sección
            </h2>
            <p className="text-zinc-400 text-sm">
              {message}
            </p>
            <p className="text-zinc-500 text-xs">
              Si tu plan personalizado aún se está generando, algunas
              secciones pueden no estar listas todavía. Puedes intentar de
              nuevo en unos minutos.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                onClick={this.handleRetry}
                className="btn-primary flex-1"
                data-testid="error-retry-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reintentar
              </Button>
              <Link to={homeTo} className="flex-1">
                <Button variant="ghost" className="btn-secondary w-full">
                  <Home className="w-4 h-4 mr-2" />
                  {homeLabel}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

export default ErrorBoundary;
